import { AntiVirusStatuses, DecryptionStatuses } from 'common/constants';

/**
 * APEX-1151: Validation status constants (aligned with file-validation-service API)
 */
export const ValidationStatuses = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  PARTIAL: 'PARTIAL',
  PROCESSING: 'PROCESSING',
} as const;

/**
 * Get human-readable label for status values
 * @param type - Status type (antiVirus, decryption, or validation)
 * @param status - Status value from backend enum
 * @returns Human-readable label
 */
export function getStatusLabel(
  type: 'antiVirus' | 'decryption' | 'validation',
  status?: string
): string {
  if (!status) return 'Unknown';

  // APEX-1151: Handle type-specific labels to avoid collision between DecryptionStatuses.SUCCESS and ValidationStatuses.SUCCESS
  if (type === 'validation') {
    const validationLabels: Record<string, string> = {
      [ValidationStatuses.SUCCESS]: 'Success',
      [ValidationStatuses.FAILED]: 'Failed',
      [ValidationStatuses.PARTIAL]: 'Partially Valid',
      [ValidationStatuses.PROCESSING]: 'Validating...',
    };
    return validationLabels[status] || status;
  }

  if (type === 'decryption') {
    const decryptionLabels: Record<string, string> = {
      [DecryptionStatuses.NOT_APPLICABLE]: 'Not Encrypted',
      [DecryptionStatuses.REQUESTED]: 'Decryption Requested',
      [DecryptionStatuses.IN_PROGRESS]: 'Decrypting...',
      [DecryptionStatuses.SUCCESS]: 'Decrypted ✓',
      [DecryptionStatuses.FAILED]: 'Decryption Failed ✗',
    };
    return decryptionLabels[status] || status;
  }

  if (type === 'antiVirus') {
    const antiVirusLabels: Record<string, string> = {
      [AntiVirusStatuses.SCANNING]: 'Scanning',
      [AntiVirusStatuses.SAFE]: 'Clean',
      [AntiVirusStatuses.INFECTED]: 'Infected',
    };
    return antiVirusLabels[status] || status;
  }

  return status;
}

/**
 * Get Chakra UI color scheme for status badge
 * @param type - Status type (antiVirus, decryption, or validation)
 * @param status - Status value from backend enum
 * @returns Chakra UI color scheme
 */
export function getBadgeColor(
  type: 'antiVirus' | 'decryption' | 'validation',
  status?: string
): 'green' | 'red' | 'blue' | 'gray' | 'yellow' {
  if (!status) return 'gray';

  // Success states - green
  if (
    status === AntiVirusStatuses.SAFE ||
    status === DecryptionStatuses.SUCCESS ||
    status === DecryptionStatuses.NOT_APPLICABLE ||  // Not encrypted is also success
    status === ValidationStatuses.SUCCESS  // APEX-1151: Validation success
  ) {
    return 'green';
  }

  // Error states - red
  if (
    status === AntiVirusStatuses.INFECTED ||
    status === DecryptionStatuses.FAILED ||
    status === ValidationStatuses.FAILED  // APEX-1151: Validation failed
  ) {
    return 'red';
  }

  // Warning states - yellow (APEX-1151: Partial validation)
  if (status === ValidationStatuses.PARTIAL) {
    return 'yellow';
  }

  // In-progress/scanning states - blue
  if (
    status === AntiVirusStatuses.SCANNING ||
    status === DecryptionStatuses.REQUESTED ||
    status === DecryptionStatuses.IN_PROGRESS ||
    status === ValidationStatuses.PROCESSING  // APEX-1151: Validation in progress
  ) {
    return 'blue';
  }

  return 'gray';
}

/**
 * Check if file is valid (anti-virus and optional decryption check)
 * @param antiVirusStatus - Anti-virus scan status
 * @param decryptionStatus - Optional decryption status
 * @returns True if file is valid and can proceed
 */
export function isFileValid(
  antiVirusStatus?: string,
  decryptionStatus?: string
): boolean {
  // Anti-virus must be SAFE
  const antiVirusOk = antiVirusStatus === AntiVirusStatuses.SAFE;

  // If decryption status is provided, check it
  if (decryptionStatus !== undefined) {
    const decryptionOk =
      decryptionStatus === DecryptionStatuses.SUCCESS ||
      decryptionStatus === DecryptionStatuses.NOT_APPLICABLE;
    return antiVirusOk && decryptionOk;
  }

  // If no decryption status provided, only check anti-virus
  return antiVirusOk;
}

/**
 * Check if should continue polling for status updates
 * @param antiVirusStatus - Anti-virus scan status
 * @param decryptionStatus - Optional decryption status
 * @returns True if should continue polling
 */
export function shouldPoll(
  antiVirusStatus?: string,
  decryptionStatus?: string
): boolean {
  // Stop polling immediately on error states to surface failures to the UI
  if (
    antiVirusStatus === AntiVirusStatuses.INFECTED ||
    decryptionStatus === DecryptionStatuses.FAILED
  ) {
    return false;
  }

  // Continue polling if anti-virus is scanning
  const isScanning = antiVirusStatus === AntiVirusStatuses.SCANNING;

  // Continue polling if decryption is in progress (if decryption status provided)
  const isDecrypting =
    decryptionStatus === DecryptionStatuses.REQUESTED ||
    decryptionStatus === DecryptionStatuses.IN_PROGRESS;

  return isScanning || isDecrypting;
}

/**
 * Format file size in bytes to human-readable string
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.3 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Check if decryption status indicates an error state
 * @param decryptionStatus - Decryption status
 * @returns True if decryption failed
 */
export function hasDecryptionError(decryptionStatus?: string): boolean {
  return decryptionStatus === DecryptionStatuses.FAILED;
}

/**
 * Check if anti-virus status indicates an error state
 * @param antiVirusStatus - Anti-virus scan status
 * @returns True if virus detected
 */
export function hasAntiVirusError(antiVirusStatus?: string): boolean {
  return antiVirusStatus === AntiVirusStatuses.INFECTED;
}

/**
 * Get appropriate error message for failed status
 * @param type - Status type (antiVirus or decryption)
 * @param status - Status value
 * @returns Error message string
 */
export function getErrorMessage(
  type: 'antiVirus' | 'decryption',
  status?: string
): string {
  if (type === 'antiVirus' && status === AntiVirusStatuses.INFECTED) {
    return 'Security threat detected. This file cannot be processed.';
  }

  if (type === 'decryption' && status === DecryptionStatuses.FAILED) {
    return 'Please reach out to your Business Unit Admin to check the Payroll File Settings configuration.';
  }

  return 'An error occurred processing this file.';
}

/**
 * Check if error response indicates infected file from document-repository-service
 * APEX-1050: Detects 403 error with code "031.S01.403.08" to stop polling
 * @param error - RTK Query error object from getDocument mutation
 * @returns True if error indicates infected file
 */
export function isInfectedFileError(error: any): boolean {
  if (!error) return false;

  // Check for 403 Forbidden status
  if (error.status !== 403) return false;

  // Parse error response structure: { errors: [{ code, message }] }
  const errorCode = error.data?.errors?.[0]?.code;

  // Backend returns this specific code for infected files
  return errorCode === '031.S01.403.08';
}

/**
 * APEX-1151: Check if validation status indicates success
 * @param validationStatus - Validation status from file-validation-service
 * @returns True if validation passed
 */
export function isValidationValid(validationStatus?: string): boolean {
  return validationStatus === ValidationStatuses.SUCCESS;
}

/**
 * APEX-1151: Check if should continue polling for validation results
 * @param validationStatus - Validation status from file-validation-service
 * @returns True if should continue polling
 */
export function shouldPollValidation(validationStatus?: string): boolean {
  return validationStatus === ValidationStatuses.PROCESSING;
}

/**
 * APEX-1151: Map API error to InProgress status for validation
 * When validation API returns error (404/500), treat as "results not ready" rather than hard failure
 * @param validationStatus - Validation status from API response
 * @param isError - Whether API call resulted in error
 * @returns Validation status (PROCESSING if error, otherwise original status)
 */
export function getValidationStatusWithError(
  validationStatus?: string,
  isError?: boolean
): string | undefined {
  // API error → treat as PROCESSING (results not ready yet, keep polling)
  if (isError) return ValidationStatuses.PROCESSING;
  return validationStatus;
}
